<template>
  <ModuleWrapper :moduleId="moduleId" title="Tracking Pixel Snippet" @initializeData="initializeData">
    <template #main>
      <div data-testId="splash-tracking-pixel-setup" class="tw-pb-8">
        <div v-if="showConsentNotice" class="tw-mx-auto tw-mb-6 tw-w-auto tw-max-w-[1000px] tw-px-2">
          <TrackingConsentNotice :showTagRule="false" />
        </div>
        <div class="tw-mx-auto tw-grid tw-w-auto tw-max-w-[1000px] tw-grid-cols-1 tw-gap-y-10">
          <div class="tw-w-full tw-px-2">
            <CodeEditorWrapper
              :key="isFetchingScript ? 'abc' : 'xyz'"
              v-bind="state"
              :codeAreaKey="codeAreaKey"
              :characterLimit="4096"
              @save="({ onSuccess, onFailure, doc }) => handleSave(onSuccess, onFailure, doc)"
              @edit="isEditing = true"
              @cancel="isEditing = false"
            />
          </div>
        </div>
      </div>
    </template>
  </ModuleWrapper>
</template>

<script lang="ts">
import {
  createUpdateSplashTrackingPixelSnippet,
  updateSplashTrackingPixelSnippetStudioEventTemplate
} from '@goldcast/api/backend';
import { defineComponent } from 'vue';
import { mapGetters, mapMutations } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';

import CodeEditorWrapper from '@/studio/marketingTags/CodeEditorWrapper.vue';
import { METHOD_SEPARATOR } from '@/studio/marketingTags/marketingTags.constants';
import { extractContent, generateDoc } from '@/studio/marketingTags/marketingTags.utils';
import { CodeAreaKeys, CodeAreaConfig } from '@/studio/marketingTags/store/types';
import TrackingConsentNotice from '@/studio/marketingTags/TrackingConsentNotice.vue';

export default defineComponent({
  name: 'TrackingPixelSnippet',
  components: { ModuleWrapper, CodeEditorWrapper, TrackingConsentNotice },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isEditing: false,
      isFetchingScript: true,
      state: {
        description:
          'This function fires when the Event Landing Page (Splash Page) is loaded. Use this to add tracking pixels or other scripts to the page.',
        docStructure: {
          firstLine: '(function () {',
          content: ' // Replace this with Javascript you want to fire on Event Landing Page (Splash Page).\n\n\n',
          lastLine: '})()'
        },
        language: 'javascript',
        readOnlyRanges: [
          { fromLine: 1, toLine: 1 },
          { fromLine: 'last', toLine: 'last' }
        ],
        doc: '(function () {\n // Replace this with Javascript you want to fire on Event Landing Page (Splash Page).\n\n\n\n})()',
        restrictedKeywords: ['cookie', 'localStorage', 'sessionStorage', METHOD_SEPARATOR]
      } as CodeAreaConfig,
      codeAreaKey: 'body' as CodeAreaKeys
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('studioEventList', {
      event: 'getCurrentEvent'
    }),
    ...mapGetters('studioTemplateList', {
      isTemplateInstance: 'isTemplateInstance',
      template: 'getCurrentTemplate'
    }),
    ...mapGetters('org', {
      org: 'getCurrentOrg'
    }),
    showConsentNotice(): boolean {
      return !!this.org?.gdprTrackingConsentEnabled && !!this.org?.tracking_consent_configured;
    },
    currentEntity(): { id: string; splash_tracking_pixel_snippet?: { body_section_script?: string } } | undefined {
      return this.isTemplateInstance ? this.template : this.event;
    }
  },
  methods: {
    ...mapMutations('studioEventList', { updateEvent: 'updateEvent' }),
    ...mapMutations('studioTemplateList', { updateTemplate: 'updateTemplate' }),
    initializeData() {
      const snippet = this.currentEntity?.splash_tracking_pixel_snippet?.body_section_script;
      if (!snippet) {
        return;
      }
      this.state.docStructure.content = snippet;
      this.state.doc = generateDoc(this.state.docStructure);
      this.isFetchingScript = false;
    },
    handleSave(onSuccess: Function, onFailure: Function, doc: string) {
      if (!this.currentEntity) {
        return;
      }
      const newContent = extractContent(doc);
      const prevContent = this.state.docStructure.content;
      const updateSnippet = this.isTemplateInstance
        ? updateSplashTrackingPixelSnippetStudioEventTemplate
        : createUpdateSplashTrackingPixelSnippet;
      updateSnippet({
        id: this.currentEntity.id,
        body: {
          body_section_script: newContent
        }
      })
        .then(() => {
          onSuccess();
          this.state.docStructure.content = newContent;
          const updatedEntity = {
            ...this.currentEntity,
            splash_tracking_pixel_snippet: {
              ...this.currentEntity?.splash_tracking_pixel_snippet,
              body_section_script: newContent
            }
          };
          // updateTemplate takes the flat entity; updateEvent takes it keyed by id
          if (this.isTemplateInstance) {
            this.updateTemplate(updatedEntity);
          } else {
            this.updateEvent({ [this.currentEntity!.id]: updatedEntity });
          }
        })
        .catch(() => {
          onFailure();
          this.state.docStructure.content = prevContent;
        });
    }
  }
});
</script>
